'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageBubble } from './MessageBubble';
import { UnifiedMessageInput } from './UnifiedMessageInput';
import { TypingIndicator } from './TypingIndicator';
import { ProcessingIndicator } from './ProcessingIndicator';
import { ClientReport } from './ClientReport';
import { ReportTransition } from './ReportTransition';
import { ChatInterfaceLayout } from './ChatInterfaceLayout';
import { RecoveryBanner } from './RecoveryBanner';
import { ErrorBanner } from './ErrorBanner';
import { pushEvent, pushGenerateLead } from '@/lib/tracking';
import { saveSessionToStorage, clearStoredSession, recordCompletedSession } from '@/lib/interview/session-recovery';
import { FileText, Loader2 } from 'lucide-react';
import type { InterviewSession, InterviewFlowType, ChatMessage, NoteTakerAnalysis, PhaseId } from '@/lib/interview/types';
import { SKIP_MESSAGE } from '@/lib/interview/skip';
import { hideQuickReplyMarker } from '@/lib/interview/quick-replies';
import { withBase } from '@/lib/base-path'

interface RecoveredMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp: string;
}

interface ChatInterfaceProps {
  session: InterviewSession;
  initialMessage: string;
  flowType: InterviewFlowType;
  sessionToken?: string;
  recoveredMessages?: RecoveredMessage[];
  isRecovered?: boolean;
  recoveredStoredAt?: number;
}

const MAX_RETRIES = 2;

export function ChatInterface({
  session,
  initialMessage,
  flowType,
  sessionToken,
  recoveredMessages,
  isRecovered = false,
  recoveredStoredAt,
}: ChatInterfaceProps) {
  const getInitialMessages = (): ChatMessage[] => {
    if (recoveredMessages && recoveredMessages.length > 0) {
      return recoveredMessages.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: new Date(m.timestamp),
      }));
    }
    return [{ id: 'initial', role: 'assistant', content: initialMessage, timestamp: new Date() }];
  };

  const [messages, setMessages] = useState<ChatMessage[]>(getInitialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [analysis, setAnalysis] = useState<NoteTakerAnalysis | null>(null);
  const [sessionStatus, setSessionStatus] = useState(session.status);
  const [completionPhase, setCompletionPhase] = useState<'none' | 'thanking' | 'generating' | 'showing_report'>('none');
  const [showRecoveryBanner, setShowRecoveryBanner] = useState(isRecovered);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  // Tracks which action triggered the currently-visible error so the
  // ErrorBanner's retry button dispatches correctly. Prior code always
  // retried the last chat message, which did nothing when a finish-now
  // call failed.
  const [lastFailedAction, setLastFailedAction] = useState<'chat' | 'finish' | null>(null);
  const [currentPhase, setCurrentPhase] = useState(session.current_phase);
  const [isProcessing, setIsProcessing] = useState(false);
  const [turnProgressInPhase, setTurnProgressInPhase] = useState(0);

  // Funnel events for GA4. The phase effect also fires once on mount, which
  // records the step a resumed session picks up at.
  useEffect(() => {
    pushEvent('ai_discovery_step', { phase: currentPhase ?? undefined });
  }, [currentPhase]);

  const completionTrackedRef = useRef(session.status === 'completed');
  useEffect(() => {
    if (sessionStatus !== 'completed' || completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    pushEvent('ai_discovery_complete');
    pushGenerateLead({ email: session.contact_email });
  }, [sessionStatus, session.contact_email]);
  // Topics per phase the note-taker saw a concrete answer for; only grows.
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  const [coveredAreas, setCoveredAreas] = useState<Partial<Record<PhaseId, string[]>>>({});
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastMessageRef = useRef<string>('');

  useEffect(() => {
    if (session.status === 'active') saveSessionToStorage(session.id, flowType, sessionToken);
  }, [session.id, session.status, flowType, sessionToken]);

  useEffect(() => {
    if (sessionStatus === 'completed') {
      clearStoredSession();
      // Breadcrumb for the landing/intake page: lets us offer a "your recent
      // report" shortcut and the duplicate-email soft-gate. Stays device-local.
      recordCompletedSession({
        sessionId: session.id,
        sessionToken,
        contactEmail: session.contact_email || undefined,
      });
    }
  }, [sessionStatus, session.id, session.contact_email, sessionToken]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => { return () => { abortControllerRef.current?.abort(); }; }, []);

  const handleSendMessage = async (content: string, isRetry = false) => {
    if (!content.trim() || isLoading || sessionStatus !== 'active') return;

    setError(null);
    setQuickReplies([]);
    lastMessageRef.current = content.trim();

    if (!isRetry) {
      setMessages((prev) => [...prev, {
        id: `user-${Date.now()}`,
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      }]);
    }

    setIsLoading(true);
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(withBase('/api/interview/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: session.id, message: content.trim(), session_token: sessionToken }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error (${response.status})`);
      }

      setRetryCount(0);
      const assistantMessageId = `assistant-${Date.now()}`;
      setMessages((prev) => [...prev, {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      }]);

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'chunk') {
              setMessages((prev) => prev.map((msg) =>
                msg.id === assistantMessageId ? { ...msg, content: msg.content + data.content } : msg
              ));
            } else if (data.type === 'analysis') {
              setAnalysis(data.analysis);
              const analysed = data.analysis?.phaseAssessment?.currentPhase as PhaseId | undefined;
              const ids: string[] = data.analysis?.coveredAreas || [];
              if (analysed && ids.length > 0) {
                setCoveredAreas((prev) => ({
                  ...prev,
                  [analysed]: Array.from(new Set([...(prev[analysed] || []), ...ids])),
                }));
              }
            } else if (data.type === 'phase_transition') {
              setCurrentPhase(data.to);
              setTurnProgressInPhase(0);
            } else if (data.type === 'processing') {
              setIsProcessing(true);
            } else if (data.type === 'error') {
              // Remove streaming message and show error
              setIsProcessing(false);
              setMessages((prev) => prev.filter((msg) => msg.id !== assistantMessageId));
              setError(data.message || 'Er is een fout opgetreden tijdens het gesprek');
              setIsLoading(false);
              return;
            } else if (data.type === 'done') {
              setIsProcessing(false);
              setMessages((prev) => prev.map((msg) =>
                msg.id === assistantMessageId
                  ? { ...msg, content: hideQuickReplyMarker(msg.content), isStreaming: false }
                  : msg
              ));
              if (Array.isArray(data.quick_replies)) {
                setQuickReplies(data.quick_replies.filter((o: unknown): o is string => typeof o === 'string'));
              }
              if (data.should_complete === true) {
                setSessionStatus('completed');
                // Phased transition: thanking → generating → showing_report
                setCompletionPhase('thanking');
                setTimeout(() => setCompletionPhase('generating'), 2000);
                setTimeout(() => setCompletionPhase('showing_report'), 5000);
              }
              if (data.turn_progress_in_phase !== undefined) {
                setTurnProgressInPhase(data.turn_progress_in_phase);
              }
              if (data.current_phase) {
                setCurrentPhase(data.current_phase);
              }
            }
          } catch { /* Ignore parse errors */ }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      console.error('Failed to send message:', err);
      setMessages((prev) => prev.filter((msg) => !msg.isStreaming));

      if (retryCount < MAX_RETRIES) {
        setRetryCount((prev) => prev + 1);
        setTimeout(() => handleSendMessage(lastMessageRef.current, true), 1000 * (retryCount + 1));
        return;
      }
      setLastFailedAction('chat');
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      if (retryCount === 0 || retryCount >= MAX_RETRIES) setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleRetry = () => {
    setError(null);
    setRetryCount(0);
    if (lastFailedAction === 'finish') {
      handleFinishNow();
    } else if (lastMessageRef.current) {
      handleSendMessage(lastMessageRef.current, true);
    }
  };

  const handleFinishNow = async () => {
    if (isFinishing || sessionStatus !== 'active') return;
    setIsFinishing(true);
    setError(null);
    try {
      const response = await fetch(withBase(`/api/interview/session/${session.id}/complete`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error (${response.status})`);
      }
      setShowFinishModal(false);
      setSessionStatus('completed');
      setCompletionPhase('thanking');
      setTimeout(() => setCompletionPhase('generating'), 2000);
      setTimeout(() => setCompletionPhase('showing_report'), 5000);
    } catch (err) {
      console.error('Failed to finish interview:', err);
      setLastFailedAction('finish');
      setError(err instanceof Error ? err.message : 'Kon het rapport nu niet genereren');
      setIsFinishing(false);
    }
  };

  // Handle help questions - sends to /api/interview/help and adds response inline
  const handleHelpQuestion = async (question: string): Promise<string> => {
    // Add user's help question to messages
    const helpQuestionId = `help-q-${Date.now()}`;
    setMessages((prev) => [...prev, {
      id: helpQuestionId,
      role: 'user',
      content: question,
      timestamp: new Date(),
      isHelpMessage: true,
    }]);

    try {
      const response = await fetch(withBase('/api/interview/help'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: session.id,
          question: question,
          session_token: sessionToken,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get help');
      }

      const data = await response.json();
      const answer = data.answer || 'Sorry, ik kon die vraag niet verwerken.';

      // Add help response to messages
      setMessages((prev) => [...prev, {
        id: `help-a-${Date.now()}`,
        role: 'assistant',
        content: answer,
        timestamp: new Date(),
        isHelpMessage: true,
      }]);

      return answer;
    } catch (err) {
      console.error('Help question error:', err);
      const errorMsg = 'Sorry, ik kon die vraag niet verwerken. Probeer het opnieuw.';

      setMessages((prev) => [...prev, {
        id: `help-a-${Date.now()}`,
        role: 'assistant',
        content: errorMsg,
        timestamp: new Date(),
        isHelpMessage: true,
      }]);

      return errorMsg;
    }
  };

  if (completionPhase === 'showing_report') {
    return (
      <ClientReport
        sessionId={session.id}
        sessionToken={sessionToken}
        contactName={session.contact_name || undefined}
        companyName={session.company_name || undefined}
      />
    );
  }

  if (completionPhase === 'generating') {
    return <ReportTransition />;
  }

  const isCompleted = sessionStatus === 'completed';
  // Shortcut availability: once past initial kennismaking (deep_dive/summary)
  // OR after ≥5 user turns regardless of phase. The turn-based fallback gives
  // users an escape hatch if they're stuck in discovery after sharing plenty.
  const userTurnCount = messages.filter((m) => m.role === 'user').length;
  const canFinishEarly =
    sessionStatus === 'active' &&
    (currentPhase === 'deep_dive' || currentPhase === 'summary' || userTurnCount >= 5);

  return (
    <ChatInterfaceLayout
      sessionId={session.id}
      flowType={flowType}
      topics={[]}
      analysis={analysis}
      currentPhase={currentPhase}
      isCompleting={completionPhase === 'thanking'}
      turnProgressInPhase={turnProgressInPhase}
      coveredAreas={coveredAreas}
      title={currentPhase === 'deep_dive' ? 'Verdieping' : currentPhase === 'summary' ? 'Afsluiting' : 'Kennismaking'}
    >
      <div className="flex flex-col h-full">
        {showRecoveryBanner && (
          <RecoveryBanner
            onDismiss={() => setShowRecoveryBanner(false)}
            storedAt={recoveredStoredAt}
          />
        )}

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-6 space-y-4 bg-off-white">
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isHelpMessage={message.isHelpMessage}
            />
          ))}
          {isLoading && !messages.some((m) => m.isStreaming) && !isProcessing && <TypingIndicator />}
          {isProcessing && <ProcessingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {error && <ErrorBanner error={error} onRetry={handleRetry} onDismiss={() => setError(null)} />}

        <div className="flex-shrink-0 px-4 py-4 border-t border-off-black/10 bg-off-white">
          {isCompleted ? (
            <div className="text-center py-4">
              <p className="text-off-black/60 mb-4">Bedankt voor het gesprek! We nemen snel contact met jullie op.</p>
              <a href={withBase("/")} className="inline-block px-6 py-2 bg-orange-cta hover:bg-orange-hover text-white rounded-xl transition-colors">
                Terug naar Home
              </a>
            </div>
          ) : (
            <div className="space-y-3">
              {quickReplies.length > 0 && !isLoading && (
                <div className="flex flex-wrap gap-2" aria-label="Snelle antwoorden">
                  {quickReplies.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleSendMessage(option)}
                      className="px-3.5 py-1.5 rounded-full border border-off-black/15 bg-white text-sm text-off-black hover:border-off-black/40 hover:bg-off-black/5 transition-colors"
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
              <UnifiedMessageInput
                onSend={handleSendMessage}
                onHelpQuestion={handleHelpQuestion}
                disabled={isLoading}
                placeholder="Typ je bericht..."
              />
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => handleSendMessage(SKIP_MESSAGE)}
                  disabled={isLoading}
                  className="text-sm text-off-black/60 hover:text-off-black underline underline-offset-4 decoration-off-black/20 disabled:opacity-40"
                >
                  Vraag overslaan
                </button>
                {canFinishEarly && (
                  <button
                    type="button"
                    onClick={() => setShowFinishModal(true)}
                    disabled={isLoading || isFinishing}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-orange-cta hover:bg-orange-hover text-white shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <FileText className="w-4 h-4" />
                    Rapport nu genereren
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showFinishModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="finish-modal-title"
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !isFinishing) setShowFinishModal(false);
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isFinishing) setShowFinishModal(false);
          }}
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-sm mx-4 p-6">
            <h3 id="finish-modal-title" className="font-display font-light text-2xl text-off-black mb-2">
              Klaar met het gesprek?
            </h3>
            <p className="text-sm text-off-black/60 mb-6">
              We stellen het rapport samen op basis van wat je tot nu toe hebt gedeeld. Je kunt het gesprek daarna niet meer voortzetten.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowFinishModal(false)}
                disabled={isFinishing}
                className="px-4 py-2 text-sm rounded-xl border border-off-black/15 hover:bg-off-black/5 transition-colors disabled:opacity-50"
              >
                Verder praten
              </button>
              <button
                onClick={handleFinishNow}
                disabled={isFinishing}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-orange-cta hover:bg-orange-hover text-white transition-colors disabled:opacity-60"
              >
                {isFinishing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Bezig...
                  </>
                ) : (
                  'Rapport genereren'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </ChatInterfaceLayout>
  );
}
